import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [deleting, setDeleting] = useState(null);

  const load = () => api.get('/payments').then(r => setPayments(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (p) => {
    if (!confirm(`Delete this ${p.status} payment (${p.currency} ${p.amount} · ${p.program || '—'})? This cannot be undone.`)) return;
    setDeleting(p.id);
    try {
      await api.delete(`/payments/${p.id}`);
      setPayments(prev => prev.filter(x => x.id !== p.id));
      toast.success('Payment deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setDeleting(null); }
  };

  const succeeded = payments.filter(p => p.status === 'succeeded');
  const total = succeeded.reduce((s, p) => s + p.amount, 0);

  return (
    <div className="space-y-6" data-testid="admin-payments">
      <div>
        <div className="label-mini text-[var(--c-green-ink)]">Revenue</div>
        <h1 className="font-display font-black text-4xl mt-1">Payments</h1>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="regen-card-elevated p-5"><div className="label-mini">Total Revenue</div><div className="font-mono text-3xl mt-2">${total.toLocaleString()}</div></div>
        <div className="regen-card-elevated p-5"><div className="label-mini">Successful</div><div className="font-mono text-3xl mt-2 text-[var(--c-green-ink)]">{succeeded.length}</div></div>
        <div className="regen-card-elevated p-5"><div className="label-mini">Pending</div><div className="font-mono text-3xl mt-2 text-[#FFB800]">{payments.filter(p => p.status === 'pending').length}</div></div>
      </div>

      <div className="regen-card-elevated overflow-hidden">
        <div className="grid grid-cols-8 px-5 py-3 label-mini border-b border-[var(--c-hair)]">
          <div className="col-span-2">Date</div>
          <div>Program</div>
          <div>Plan</div>
          <div>Currency</div>
          <div>Amount</div>
          <div>Status</div>
          <div className="text-right">Action</div>
        </div>
        {payments.map(p => (
          <div key={p.id} className="grid grid-cols-8 px-5 py-3 items-center border-b border-[var(--c-hair)] last:border-0 hover:bg-[var(--c-film)]" data-testid={`payment-${p.id}`}>
            <div className="col-span-2 text-sm text-muted-foreground font-mono">{format(new Date(p.created_at), 'MMM d, HH:mm')}</div>
            <div className="text-sm">{p.program}</div>
            <div className="text-sm">{p.plan_type}</div>
            <div className="text-sm font-mono">{p.currency}</div>
            <div className="font-mono">{p.amount}</div>
            <div><span className={`pill ${p.status === 'succeeded' ? 'bg-[var(--c-green-soft)] text-[var(--c-green-ink)]' : p.status === 'pending' ? 'bg-[#FFB800]/10 text-[#FFB800]' : 'bg-red-500/10 text-red-400'}`}>{p.status}</span></div>
            <div className="text-right">
              <button onClick={() => del(p)} disabled={deleting === p.id} data-testid={`delete-payment-${p.id}`}
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition disabled:opacity-40"
                title="Delete payment">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {payments.length === 0 && <div className="p-10 text-center text-muted-foreground text-sm">No payments yet.</div>}
      </div>
    </div>
  );
}
